// tracking.js — pure helpers for signup conversion tracking (postMessage to the
// embedding site, ad-platform attribution capture, thank-you redirects).
//
// Everything here is deliberately framework-free and side-effect-free (except the
// explicitly-named sessionStorage helpers) so it can be unit-tested with node.
//
// PRIVACY RULE (from the marketing handoff, and it's the right rule): nothing that
// leaves the page through postMessage or a redirect URL may contain PII — no name,
// email, phone, address, or card data. Plan, dogs, value, zip, and a random uuid only.
// Identifiers that DO travel server-side are SHA-256 hashed first.

// "twice-weekly" (our internal value) → "twice_weekly" (the ad-platform-facing slug).
export function planSlug(frequency) {
  return String(frequency || "").trim().toLowerCase().replace(/-/g, "_");
}

// The attribution params worth persisting.
//
// gbraid/wbraid matter as much as gclid: Google issues them instead of gclid when
// iOS privacy rules block the normal click id (gbraid = app→web, wbraid = web→web).
// Capture all three — any one of them may be the only identifier present, and a
// conversion with none of them can never be imported back into Google Ads.
// msclkid is the same idea on Microsoft/Bing Ads.
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid"];
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
// Not click ids — Google's auto-tagging companions. gad_source says which surface the
// click came from (Search vs Performance Max etc.) and gclsrc distinguishes a plain
// Ads click from a Display & Video one; both are useful for reading a report and
// neither can be reconstructed later, so capture them alongside.
const META_KEYS = ["gad_source", "gclsrc"];
const ALL_URL_KEYS = [...UTM_KEYS, ...CLICK_KEYS, ...META_KEYS];

const STORAGE_KEY = "scoop_attribution";

// Collect ad attribution from a query string + document.cookie string.
// Returns a plain object with only the keys actually present, plus landing_url and
// click_timestamp — or null when nothing was captured (so the DB column stays NULL
// instead of storing {}).
export function collectAttribution(search, cookieStr, landingUrl, nowISO) {
  const out = {};
  try {
    const params = new URLSearchParams(search || "");
    for (const k of ALL_URL_KEYS) {
      const v = (params.get(k) || "").trim();
      if (v) out[k] = v.slice(0, 500); // click ids are long; cap defensively
    }
  } catch { /* malformed search string — capture nothing from it */ }
  try {
    // Meta's first-party cookies: _fbp (browser id) and _fbc (click id echo).
    for (const part of String(cookieStr || "").split(";")) {
      const eq = part.indexOf("=");
      if (eq === -1) continue;
      const name = part.slice(0, eq).trim();
      const val = part.slice(eq + 1).trim();
      if (name === "_fbp" && val) out.fbp = val.slice(0, 500);
      if (name === "_fbc" && val) out.fbc = val.slice(0, 500);
    }
  } catch { /* no cookie access — fine */ }
  if (Object.keys(out).length === 0) return null;
  // Google's offline conversion import requires the CLICK time, not the conversion
  // time. Stamped at first touch and carried forward untouched.
  out.click_timestamp = nowISO || new Date().toISOString();
  if (landingUrl) out.landing_url = String(landingUrl).slice(0, 1000);
  return out;
}

// True when the object carries at least one ad-platform click identifier — i.e.
// this signup is attributable to a paid click.
export function hasClickId(attr) {
  return !!attr && CLICK_KEYS.some((k) => !!attr[k]);
}

// ── Cross-step persistence ────────────────────────────────────────────────────
// A visitor lands on step 1 with ?gclid=… and converts on step 3. The params are
// gone from the URL the moment anything rewrites it, and a mid-flow reload would
// otherwise lose the attribution entirely. Session storage keeps FIRST TOUCH:
// once stored, later page loads never overwrite it.
export function persistAttribution(storage, attr) {
  if (!storage) return attr;
  try {
    const existing = storage.getItem(STORAGE_KEY);
    if (existing) return JSON.parse(existing); // first touch wins
    if (attr) storage.setItem(STORAGE_KEY, JSON.stringify(attr));
    return attr;
  } catch { return attr; } // private mode / quota — fall back to in-memory
}

export function loadAttribution(storage) {
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

// Resolve the attribution for this pageview: whatever was stored at first touch,
// else what's on this URL now (and store that).
export function resolveAttribution(storage, search, cookieStr, landingUrl, nowISO) {
  const stored = loadAttribution(storage);
  const fresh = collectAttribution(search, cookieStr, landingUrl, nowISO);
  if (!stored) return fresh ? persistAttribution(storage, fresh) : null;
  // First touch wins — EXCEPT over a stored capture that never held a click id at all.
  // The embed loads this page twice (the parent rewrites the iframe src after the
  // browser has already started the paramless one), so on a repeat visit the first
  // load can store a cookie-only {_fbp} object and the second load, the one actually
  // carrying ?gclid=, would be discarded as "not first touch". Upgrade instead:
  // a click id can never be recovered later, a cookie can.
  if (fresh && hasClickId(fresh) && !hasClickId(stored)) {
    const merged = { ...stored, ...fresh };
    try { storage && storage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch { /* private mode */ }
    return merged;
  }
  return stored;
}

// ── Enhanced Conversions ─────────────────────────────────────────────────────
// Google matches conversions to clicks using hashed user data, recovering
// conversions that cookie restrictions would otherwise drop. Hash browser-side so
// the raw values never reach the ad platform.
export function normalizeEmail(email) {
  const normalized = String(email || "").trim().toLowerCase();
  const at = normalized.lastIndexOf("@");
  if (at <= 0) return normalized;
  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  // Google's enhanced-conversion normalization requires periods before Gmail /
  // Googlemail domains to be removed before SHA-256 hashing.
  if (domain === "gmail.com" || domain === "googlemail.com") {
    return `${local.replace(/\./g, "")}@${domain}`;
  }
  return normalized;
}

// US 10-digit → E.164. Returns null when it can't be normalized — a hash of a
// malformed number is worse than no hash, because it never matches anything.
export function normalizePhoneE164(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return null;
}

export async function sha256Hex(value) {
  if (!value) return null;
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch { return null; } // no subtle crypto (insecure origin) — skip, don't break checkout
}

// Build the thank-you redirect URL: append our params to whatever the operator
// configured, preserving any query string already on it.
export function buildThankYouUrl(base, { event_id, plan, dogs, value }) {
  try {
    const u = new URL(base);
    u.searchParams.set("signup", "1");
    if (event_id) u.searchParams.set("event_id", event_id);
    if (plan) u.searchParams.set("plan", plan);
    if (dogs != null) u.searchParams.set("dogs", String(dogs));
    if (value != null) u.searchParams.set("value", String(value));
    return u.toString();
  } catch { return null; } // operator typo'd the URL — no redirect beats a broken one
}

// Parse the operator's allowed-embed-origins setting ("https://a.com, https://b.com")
// into clean origins. Invalid entries are dropped rather than passed to postMessage.
export function parseAllowedOrigins(setting) {
  return String(setting || "")
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => { try { return new URL(s).origin; } catch { return null; } })
    .filter(Boolean);
}

// Granular funnel analytics (handoff v5): the form narrates its own interactions
// to the embedding page, whose generic forwarder lands them in GA4 (the form is a
// cross-origin iframe, so the parent's GA4 can't see inside it any other way).
// NO PII in values — option labels and field NAMES only; these reach GA4 verbatim.
export const msgAnalyticsStep = (step) => ({
  source: "iscoopcrm", type: "scoop-signup-step", step,
  name: step === 1 ? "address" : step === 2 ? "plan" : "payment",
});
export const msgAction = (action, value) => ({
  source: "iscoopcrm", type: "scoop-signup-action", action,
  ...(value != null && value !== "" ? { value: String(value).slice(0, 40) } : {}),
});

// The postMessage payloads, exactly per the marketing handoff's contract.
// `source: "iscoopcrm"` is the discriminator the parent listener filters on.
export const msgResize = (height) => ({ source: "iscoopcrm", type: "resize", height });
// ⚠️ LOAD-BEARING for the parent page's ad conversions (begin_checkout /
// InitiateCheckout fire off this exact shape). Never remove or rename without a
// coordinated WordPress bridge release.
export const msgStep = (step) => ({
  source: "iscoopcrm", type: "step", step,
  name: step === 1 ? "address" : step === 2 ? "plan" : "payment",
});
export const msgOutOfArea = (zip) => ({ source: "iscoopcrm", type: "out_of_area", zip: String(zip || "") });
export const msgSignupComplete = ({ event_id, frequency, dogs, monthly_value, card_on_file, user_data }) => ({
  source: "iscoopcrm", type: "signup_complete",
  event_id,
  // SHA-256 email/phone for the parent's Enhanced Conversions. Only ever populated
  // when the operator configured an explicit embed origin — never under the "*"
  // fallback — and hashes only: the raw values still never leave this frame.
  ...(user_data ? { user_data } : {}),
  plan: planSlug(frequency),
  dogs: Number(dogs) || 1,
  monthly_value: Number(monthly_value) || 0,
  currency: "USD",
  card_on_file: !!card_on_file, // extra (non-PII): instant activation vs email-me-a-link
});

// A submitted registration that still needs a card is a useful funnel event, but
// it is NOT a purchase/subscription and must never train ad bidding as one.
export const msgSignupPending = ({ event_id, frequency, dogs, monthly_value }) => ({
  source: "iscoopcrm", type: "signup_pending_payment",
  event_id,
  plan: planSlug(frequency),
  dogs: Number(dogs) || 1,
  monthly_value: Number(monthly_value) || 0,
  currency: "USD",
});
